import { useState } from 'react';
import { Alert } from '@/components/Alert';
import type { TipoEquipamentoMovimentacao } from '../../../models';
import {
  SISTEMA_OPERACIONAL_PRINCIPAL_LABELS,
  TIPO_EQUIPAMENTO_LABELS,
  type SistemaOperacionalPrincipal,
} from '../../SolicitacaoDetailsPage/solicitacaoDetailsPage.definitions';

/** O que se pede em "Solicitação de Periféricos e Acessórios" — computador (Windows/macOS) vai
 * por Troca de Equipamento / Nova Demanda, não por aqui. */
const TIPOS_PEDIDO: TipoEquipamentoMovimentacao[] = ['monitor', 'smartphone', 'chip', 'periferico', 'cartao_acesso', 'outros'];
const SO_SMARTPHONE: SistemaOperacionalPrincipal[] = ['android', 'ios'];

type SoFormulario = SistemaOperacionalPrincipal | 'sem_so' | '';

interface TipoEquipamentoPedidoProps {
  /** Mesmos campos do formulário (SO primeiro nos outros tipos) — aqui o tipo vem primeiro e
   * só o smartphone pergunta o SO; o resto vira "sem SO" + tipo, igual ao fluxo de sempre. */
  soPrincipal: SoFormulario;
  tipoSemSo: TipoEquipamentoMovimentacao | '';
  onChange: (valor: { soPrincipal: SoFormulario; tipoSemSo: TipoEquipamentoMovimentacao | '' }) => void;
  /** Periférico/Outros: qual item (ex.: "teclado"). Vai em descricaoLivre do item. */
  descricao: string;
  onChangeDescricao: (valor: string) => void;
}

/** Tipos genéricos — pedem "Qual item?" (obrigatório). */
export const TIPOS_PEDIDO_COM_DESCRICAO: TipoEquipamentoMovimentacao[] = ['periferico', 'outros'];

export default function TipoEquipamentoPedido({
  soPrincipal,
  tipoSemSo,
  onChange,
  descricao,
  onChangeDescricao,
}: TipoEquipamentoPedidoProps) {
  const ehSmartphone = soPrincipal === 'android' || soPrincipal === 'ios';
  // Smartphone escolhido mas SO ainda não — guarda a escolha até o SO vir.
  const [smartphoneSemSo, setSmartphoneSemSo] = useState(false);
  const tipo = ehSmartphone || smartphoneSemSo ? 'smartphone' : tipoSemSo;

  const escolherTipo = (valor: TipoEquipamentoMovimentacao | '') => {
    if (valor === 'smartphone') {
      setSmartphoneSemSo(true);
      onChange({ soPrincipal: '', tipoSemSo: '' });
      return;
    }
    setSmartphoneSemSo(false);
    onChange({ soPrincipal: valor ? 'sem_so' : '', tipoSemSo: valor });
  };

  return (
    <>
      {/* Pedido não é garantia de entrega — evita a expectativa de "pedi, vou receber". */}
      <Alert
        variant="info"
        title="Sujeito à disponibilidade"
        description="O pedido passa pela avaliação do time de Segurança da Informação e depende da disponibilidade do item em estoque. Você acompanha o andamento por esta solicitação."
      />

      <div>
        <label className="block text-sm font-medium text-gray-700">Tipo de Equipamento *</label>
        <select
          className="input-primary mt-1 w-full"
          value={tipo}
          onChange={(e) => escolherTipo(e.target.value as TipoEquipamentoMovimentacao | '')}
        >
          <option value="">Selecione...</option>
          {TIPOS_PEDIDO.map((valor) => (
            <option key={valor} value={valor}>
              {TIPO_EQUIPAMENTO_LABELS[valor]}
            </option>
          ))}
        </select>
      </div>

      {tipo && TIPOS_PEDIDO_COM_DESCRICAO.includes(tipo) && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Qual item? *</label>
          <input
            type="text"
            className="input-primary mt-1 w-full"
            placeholder={tipo === 'periferico' ? 'Ex.: teclado, mouse, adaptador' : 'Descreva o item'}
            value={descricao}
            onChange={(e) => onChangeDescricao(e.target.value)}
          />
        </div>
      )}

      {tipo === 'smartphone' && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Qual Sistema Operacional? *</label>
          <select
            className="input-primary mt-1 w-full"
            value={ehSmartphone ? soPrincipal : ''}
            onChange={(e) => onChange({ soPrincipal: e.target.value as SoFormulario, tipoSemSo: '' })}
          >
            <option value="">Selecione...</option>
            {SO_SMARTPHONE.map((valor) => (
              <option key={valor} value={valor}>
                {SISTEMA_OPERACIONAL_PRINCIPAL_LABELS[valor]}
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );
}
